import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtNum, fmtDate, BACKEND_URL } from "../lib/api";
import { PageHeader, Surface, Btn, Field, Input, Select, StatusTag, Textarea, CurrencyInput } from "../components/Primitives";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Plus, Trash, Printer, FileArrowUp, FileArrowDown, Sparkle, Camera, FileText } from "@phosphor-icons/react";
import MaintenanceTab from "../components/MaintenanceTab";
import AirworthinessTab from "../components/AirworthinessTab";
import WorkOrdersTab from "../components/WorkOrdersTab";
import AIAssistantTab from "../components/AIAssistantTab";
import AircraftDocumentsTab from "../components/AircraftDocumentsTab";
import DocAIUploadModal from "../components/DocAIUploadModal";
import CompletenessCard from "../components/CompletenessCard";
import HobbsPhotoModal from "../components/HobbsPhotoModal";
import { useI18n } from "../contexts/I18nContext";

// Phase 4H — Pro-tier tabs are hidden unless the tenant has cost-sharing
// access. The free/Standard set ALWAYS sees: Overview, Profile, Maintenance,
// Airworthiness, Work orders, AI Assistant, Documents, Members.
const FREE_TABS = [
  "Overview", "Profile", "Maintenance", "Airworthiness",
  "Work orders", "AI Assistant", "Documents", "Members",
];
const PRO_TABS = ["Reserves", "Cost rates", "Bank", "Expenses", "Contributions"];

export default function AircraftDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, tenant } = useAuth();
  const { lang: reportLang } = useI18n();
  const isOwner = user?.role === "owner" || user?.role === "tenant_admin";
  const hasCostShare = !!user?.has_cost_sharing_access || !!user?.platform_superuser;
  const TABS = hasCostShare ? [...FREE_TABS, ...PRO_TABS] : FREE_TABS;
  const [tab, setTab] = useState("Overview");
  const [hobbsModalOpen, setHobbsModalOpen] = useState(false);
  const [a, setA] = useState(null);
  const [dash, setDash] = useState(null);
  const [members, setMembers] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [exForm, setExForm] = useState({ date: new Date().toISOString().substring(0, 10), category: "fuel", amount_amount: 0, description: "" });
  const [memForm, setMemForm] = useState({ user_id: "", named_on_insurance: true, onboarding_complete: false, status: "pending" });
  const [contractFor, setContractFor] = useState(null); // membership being uploaded
  const [contractForm, setContractForm] = useState({ signed_date: new Date().toISOString().substring(0, 10), expiry_date: "", file: null });
  const [contractMsg, setContractMsg] = useState("");
  const [contributions, setContributions] = useState([]);
  const [cForm, setCForm] = useState({ user_id: "", amount_amount: 0, bucket: "engine", kind: "initial", date: new Date().toISOString().substring(0, 10), notes: "" });
  const [enums, setEnums] = useState({ operating_regions: [], aircraft_statuses: [], fuel_types: [], document_categories: [] });
  const [docAIOpen, setDocAIOpen] = useState(false);

  const load = () => Promise.all([
    api.get(`/aircraft/${id}`).then((r) => setA(r.data)),
    api.get(`/aircraft/${id}/dashboard`).then((r) => setDash(r.data)),
    api.get(`/aircraft/${id}/memberships`).then((r) => setMembers(r.data)),
    api.get(`/expenses?aircraft_id=${id}`).then((r) => setExpenses(r.data)),
    api.get(`/participants`).then((r) => setParticipants(r.data)),
    api.get(`/aircraft/${id}/contributions`).then((r) => setContributions(r.data)),
    api.get(`/aircraft-enums`).then((r) => setEnums(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!a || !dash) return null;

  const saveAircraft = async () => {
    await api.patch(`/aircraft/${id}`, {
      make_model: a.make_model, registration: a.registration, home_base: a.home_base,
      status: a.status, fuel_type: a.fuel_type, fuel_capacity: a.fuel_capacity,
      hangar_location: a.hangar_location, insurance_provider: a.insurance_provider,
      insurance_expiry: a.insurance_expiry, insurance_policy_number: a.insurance_policy_number,
      camo_provider: a.camo_provider, arc_expiry: a.arc_expiry, notes: a.notes,
      accounting_start_date: a.accounting_start_date || null,
      max_members: a.max_members != null ? Number(a.max_members) : null,
      named_owner_user_id: a.named_owner_user_id || null,
    });
    load();
  };
  // Phase 1B — extended profile save
  const saveProfile = async () => {
    const fields = [
      "manufacturer", "model", "country_of_registration", "operating_region",
      "serial_number", "year_of_manufacture", "type_certificate_ref",
      "airframe_total_hours", "total_cycles", "total_landings", "tach_time",
      "engine_manufacturer", "engine_model", "engine_serial",
      "engine_total_hours", "engine_tbo_hours", "engine_tbo_calendar",
      "propeller_manufacturer", "propeller_model", "propeller_serial",
      "propeller_total_hours", "propeller_overhaul_due",
      "avionics", "stcs",
      "registration_expiry", "last_annual_inspection", "next_annual_due",
      "last_100h_inspection", "elt_expiry", "survival_kit_expiry",
      "transponder_check_due", "altimeter_check_due", "pitot_static_check_due",
      "maintenance_provider", "default_currency",
    ];
    const payload = {};
    fields.forEach((k) => {
      if (a[k] === undefined) return;
      // Coerce empty strings to null so backend validators don't choke
      if (typeof a[k] === "string" && a[k].trim() === "") {
        payload[k] = null;
      } else {
        payload[k] = a[k];
      }
    });
    await api.patch(`/aircraft/${id}`, payload);
    load();
  };
  // Avionics + STC helpers (Phase 1B)
  const addAvionics = () => setA({ ...a, avionics: [...(a.avionics || []), { name: "", serial: "" }] });

  // Phase 6A — Apply OCR'd meter reading to the aircraft
  const applyMeterReading = async (reading, meterType) => {
    const patch = {};
    if (meterType === "tach") patch.tach_time = reading;
    else patch.hobbs = reading;
    try {
      await api.patch(`/aircraft/${id}`, patch);
      await load();
    } catch (e) {
      console.error("apply meter reading failed", e);
    }
  };
  const updateAvionics = (idx, patch) => {
    const next = [...(a.avionics || [])];
    next[idx] = { ...next[idx], ...patch };
    setA({ ...a, avionics: next });
  };
  const removeAvionics = (idx) => setA({ ...a, avionics: (a.avionics || []).filter((_, i) => i !== idx) });
  const addStc = () => setA({ ...a, stcs: [...(a.stcs || []), { reference: "", description: "" }] });
  const updateStc = (idx, patch) => {
    const next = [...(a.stcs || [])];
    next[idx] = { ...next[idx], ...patch };
    setA({ ...a, stcs: next });
  };
  const removeStc = (idx) => setA({ ...a, stcs: (a.stcs || []).filter((_, i) => i !== idx) });
  const saveReserves = async () => {
    await api.patch(`/aircraft/${id}/reserves`, { reserves: a.reserves, reserve_targets: a.reserve_targets });
    load();
  };
  const saveCost = async () => {
    await api.patch(`/aircraft/${id}/cost-settings`, a.cost_settings);
    load();
  };
  const saveBank = async () => {
    await api.patch(`/aircraft/${id}/bank`, a.bank_account);
    load();
  };
  const addMember = async () => {
    if (!memForm.user_id) return;
    await api.post(`/aircraft/${id}/memberships`, memForm);
    setMemForm({ user_id: "", named_on_insurance: true, onboarding_complete: false, status: "pending" });
    load();
  };
  const updateMember = async (uid, patch) => {
    await api.patch(`/aircraft/${id}/memberships/${uid}`, patch);
    load();
  };
  const removeMember = async (uid) => {
    if (!confirm("Remove this member from the aircraft?")) return;
    await api.delete(`/aircraft/${id}/memberships/${uid}`);
    load();
  };
  const addExpense = async () => {
    const payload = { date: exForm.date, category: exForm.category, description: exForm.description, aircraft_id: id };
    if (!exForm.amount_amount) return;
    payload.amount_amount = exForm.amount_amount;
    await api.post("/expenses", payload);
    setExForm({ ...exForm, amount_amount: 0, description: "" });
    load();
  };
  const removeExpense = async (eid) => {
    await api.delete(`/expenses/${eid}`);
    load();
  };

  const submitContract = async () => {
    setContractMsg("");
    if (!contractForm.file || !contractForm.expiry_date) {
      setContractMsg("File and expiry date are required");
      return;
    }
    const fd = new FormData();
    fd.append("file", contractForm.file);
    fd.append("signed_date", contractForm.signed_date);
    fd.append("expiry_date", contractForm.expiry_date);
    try {
      const r = await api.post(`/aircraft/${id}/memberships/${contractFor.user_id}/contract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setContractFor(null);
      setContractForm({ signed_date: new Date().toISOString().substring(0, 10), expiry_date: "", file: null });
      load();
      const fee = r.data?.annual_fee_amount || 0;
      if (fee > 0) alert(`Contract uploaded. Annual fee settlement of €${fee} created — see Settlements.`);
    } catch (e) {
      setContractMsg(e.response?.data?.detail || "Upload failed");
    }
  };

  const addContribution = async () => {
    if (!cForm.user_id || !cForm.amount_amount) return;
    await api.post(`/aircraft/${id}/contributions`, cForm);
    setCForm({ ...cForm, amount_amount: 0, notes: "" });
    load();
  };
  const removeContribution = async (cid) => {
    if (!confirm("Remove this contribution? Bucket balance will be reduced accordingly.")) return;
    await api.delete(`/aircraft/${id}/contributions/${cid}`);
    load();
  };

  const reserveKeys = ["engine_amount", "prop_amount", "maintenance_amount", "improvement_amount"];
  const reserveLabels = { engine_amount: "Engine", prop_amount: "Propeller", maintenance_amount: "Maintenance", improvement_amount: "Improvement" };
  const memberOptions = participants.filter((p) => !members.find((m) => m.user_id === p.user_id));

  return (
    <div>
      <PageHeader
        overline={a.home_base || "—"}
        title={`${a.registration} · ${a.make_model}`}
        subtitle={`${dash.active_members} active member${dash.active_members === 1 ? "" : "s"} · Hobbs ${fmtNum(a.hobbs, 1)} · ${a.fuel_type || "—"}`}
        actions={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={() => navigate("/fleet")}><ArrowLeft size={14} className="inline mr-1.5" />Fleet</Btn>
            {isOwner && (
              <Btn variant="secondary" onClick={() => setHobbsModalOpen(true)} data-testid="open-hobbs-photo-btn">
                <Camera size={14} className="inline mr-1.5" />Read meter from photo
              </Btn>
            )}
            <Btn
              variant="secondary"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/aircraft/${id}/status-report?lang=${encodeURIComponent(reportLang || "en")}`, "_blank", "noopener,noreferrer")}
              data-testid="open-status-report-btn"
            >
              <FileText size={14} className="inline mr-1.5" />Status report
            </Btn>
            <Btn variant="ghost" onClick={() => window.print()}><Printer size={14} className="inline mr-1.5" />Print</Btn>
            <StatusTag status={a.status} />
          </div>
        }
      />

      {isOwner && (
        <HobbsPhotoModal
          aircraftId={id}
          open={hobbsModalOpen}
          onClose={() => setHobbsModalOpen(false)}
          onApply={applyMeterReading}
        />
      )}

      <div className="px-8 pt-4 border-b border-zinc-800 flex gap-1 flex-wrap" data-testid="aircraft-tabs">
        {TABS.map((t) => (
          <button key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${
              tab === t ? "border-amber text-amber" : "border-transparent text-zinc-500 hover:text-zinc-200"
            }`}>{t}</button>
        ))}
      </div>

      <div className="p-8 space-y-6">
        {tab === "Overview" && (
          <>
            <CompletenessCard aircraftId={id} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 md:grid-cols-5 gap-4">
              <Surface className="p-5"><div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">Settlements</div><div className="text-2xl font-mono text-zinc-100">{fmtMoney(dash.total_settlements_amount)}</div><div className="text-[10px] text-zinc-500 mt-1 font-mono">agreed</div></Surface>
              <Surface className="p-5"><div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">Collected</div><div className="text-2xl font-mono text-green-400">{fmtMoney(dash.total_collected_amount)}</div><div className="text-[10px] text-zinc-500 mt-1 font-mono">confirmed payments</div></Surface>
              <Surface className="p-5"><div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">Member contribs</div><div className="text-2xl font-mono text-amber">{fmtMoney(dash.total_member_contributions_amount)}</div><div className="text-[10px] text-zinc-500 mt-1 font-mono">deposits to buckets</div></Surface>
              <Surface className="p-5"><div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">Expenses</div><div className="text-2xl font-mono">{fmtMoney(dash.total_expenses_amount)}</div><div className="text-[10px] text-zinc-500 mt-1 font-mono">recorded</div></Surface>
              <Surface className="p-5"><div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">Balance</div><div className={`text-2xl font-mono ${dash.balance_amount >= 0 ? "text-green-400" : "text-red-400"}`}>{fmtMoney(dash.balance_amount)}</div><div className="text-[10px] text-zinc-500 mt-1 font-mono">collected + contribs − expenses</div></Surface>
            </div>

            <Surface className="p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-4">Aircraft details</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Make / Model"><Input value={a.make_model} disabled={!isOwner} onChange={(e) => setA({ ...a, make_model: e.target.value })} /></Field>
                <Field label="Registration"><Input value={a.registration} disabled={!isOwner} onChange={(e) => setA({ ...a, registration: e.target.value })} /></Field>
                <Field label="Home base"><Input value={a.home_base || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, home_base: e.target.value })} /></Field>
                <Field label="Status">
                  <Select value={a.status} disabled={!isOwner} onChange={(e) => setA({ ...a, status: e.target.value })}>
                    {(enums.aircraft_statuses?.length ? enums.aircraft_statuses : ["available", "in_maintenance", "grounded", "stored", "retired"]).map((s) => (
                      <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Fuel type">
                  <Select value={a.fuel_type || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, fuel_type: e.target.value || null })}>
                    <option value="">—</option>
                    {(enums.fuel_types || []).map((f) => <option key={f} value={f}>{f.replace(/_/g, " ")}</option>)}
                  </Select>
                </Field>
                <Field label="Fuel capacity (L)"><Input type="number" value={a.fuel_capacity || 0} disabled={!isOwner} onChange={(e) => setA({ ...a, fuel_capacity: parseFloat(e.target.value || 0) })} /></Field>
                <Field label="Hangar"><Input value={a.hangar_location || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, hangar_location: e.target.value })} /></Field>
                <Field label="Insurance provider"><Input value={a.insurance_provider || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, insurance_provider: e.target.value })} /></Field>
                <Field label="Insurance policy #"><Input value={a.insurance_policy_number || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, insurance_policy_number: e.target.value })} /></Field>
                <Field label="Insurance expiry"><Input type="date" value={a.insurance_expiry || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, insurance_expiry: e.target.value })} /></Field>
                <Field label="CAMO"><Input value={a.camo_provider || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, camo_provider: e.target.value })} /></Field>
                <Field label="ARC expiry"><Input type="date" value={a.arc_expiry || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, arc_expiry: e.target.value })} /></Field>
                <Field label="Accounting starts"><Input type="date" value={a.accounting_start_date || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, accounting_start_date: e.target.value })} data-testid="accounting-start-input" /></Field>
                <Field label="Max members"><Input type="number" min="0" value={a.max_members ?? ""} disabled={!isOwner} placeholder="0 = no cap" onChange={(e) => setA({ ...a, max_members: e.target.value === "" ? null : parseInt(e.target.value, 10) })} data-testid="max-members-input" /></Field>
                <Field label="Named owner (exempt from annual fee)">
                  <Select
                    value={a.named_owner_user_id || ""}
                    disabled={!isOwner}
                    onChange={(e) => setA({ ...a, named_owner_user_id: e.target.value || null })}
                    data-testid="named-owner-select"
                  >
                    <option value="">— None —</option>
                    {members.map((m) => (
                      <option key={m.user_id} value={m.user_id}>{m.user_name || m.user_id}</option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className="mt-4"><Field label="Notes"><Textarea rows={2} value={a.notes || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, notes: e.target.value })} /></Field></div>
              {isOwner && <div className="mt-4 flex justify-end"><Btn onClick={saveAircraft} data-testid="save-aircraft-btn">Save aircraft</Btn></div>}
            </Surface>

            {dash.annual_projection && (
              <Surface className="p-6" data-testid="annual-projection">
                <div className="flex justify-between items-baseline mb-4 flex-wrap gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Annual projection</div>
                    <div className="text-xs text-zinc-500 mt-1 font-mono">
                      Recurring aircraft costs (Hangar × 12 + CAMO yearly + Maintenance bucket annual target){" "}
                      {dash.accounting_start_date ? `· accounting from ${dash.accounting_start_date}` : "· accounting start not set"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl text-amber font-mono">{fmtMoney(dash.annual_projection.total_amount)}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">annualised</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 md:grid-cols-5 gap-3">
                  <div className="border border-zinc-800 bg-[#0A0A0A] p-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Hangar / yr</div>
                    <div className="font-mono text-amber text-base">{fmtMoney(dash.annual_projection.hangar_amount)}</div>
                    <div className="text-[10px] text-zinc-600 font-mono">{fmtMoney(a.cost_settings?.hangar_fee_amount || 0)} × 12</div>
                  </div>
                  <div className="border border-zinc-800 bg-[#0A0A0A] p-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">CAMO / yr</div>
                    <div className="font-mono text-amber text-base">{fmtMoney(dash.annual_projection.camo_amount || 0)}</div>
                    <div className="text-[10px] text-zinc-600 font-mono">posts {fmtMoney(((dash.annual_projection.camo_amount || 0) / 12))}/mo</div>
                  </div>
                  <div className="border border-zinc-800 bg-[#0A0A0A] p-3" data-testid="projection-maintenance-tile">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Maintenance / yr</div>
                    <div className="font-mono text-amber text-base">{fmtMoney(dash.annual_projection.maintenance_amount || 0)}</div>
                    <div className="text-[10px] text-zinc-600 font-mono">bucket target</div>
                    {(() => {
                      const target = dash.reserve_targets?.maintenance_amount || 0;
                      const current = dash.reserves?.maintenance_amount || 0;
                      if (!target) return null;
                      const pct = Math.max(0, Math.min(100, Math.round((current / target) * 100)));
                      const deficit = Math.max(0, target - current);
                      const tone = pct >= 80 ? "text-green-400" : pct >= 50 ? "text-amber" : "text-red-400";
                      return (
                        <div className={`text-[10px] mt-2 font-mono leading-tight ${tone}`} data-testid="maintenance-funding-pace">
                          {pct}% funded
                          {deficit > 0 && (
                            <span className="text-zinc-500"> — set aside {fmtMoney(deficit)} more this year</span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="border border-zinc-800 bg-[#0A0A0A] p-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Per pilot share</div>
                    <div className="font-mono text-amber text-base">
                      {dash.annual_projection.per_member_amount != null
                        ? fmtMoney(dash.annual_projection.per_member_amount)
                        : <span className="text-zinc-600">set max members</span>}
                    </div>
                    <div className="text-[10px] text-zinc-600 font-mono">total ÷ max members</div>
                  </div>
                  <div className="border border-zinc-800 bg-[#0A0A0A] p-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Members</div>
                    <div className="font-mono text-base">{dash.active_members || 0}<span className="text-zinc-600"> / {dash.max_members || "∞"}</span></div>
                    <div className="text-[10px] text-zinc-600 font-mono">active / cap</div>
                  </div>
                </div>
              </Surface>
            )}
          </>
        )}

        {tab === "Profile" && (
          <>
            <Surface className="p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Identity & registration</div>
              <div className="text-xs text-zinc-500 mb-4">Full type-certificate identity, country of registration and operating jurisdiction. Required for Phase 2 AD/SB tracking.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Manufacturer"><Input value={a.manufacturer || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, manufacturer: e.target.value })} data-testid="profile-manufacturer" /></Field>
                <Field label="Model"><Input value={a.model || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, model: e.target.value })} data-testid="profile-model" /></Field>
                <Field label="Serial number"><Input value={a.serial_number || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, serial_number: e.target.value })} data-testid="profile-serial" /></Field>
                <Field label="Year of manufacture"><Input type="number" min="1900" max="2100" value={a.year_of_manufacture ?? ""} disabled={!isOwner} onChange={(e) => setA({ ...a, year_of_manufacture: e.target.value === "" ? null : parseInt(e.target.value, 10) })} data-testid="profile-yom" /></Field>
                <Field label="Type-certificate ref"><Input value={a.type_certificate_ref || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, type_certificate_ref: e.target.value })} data-testid="profile-tcref" /></Field>
                <Field label="Country of registration" hint="ISO 3166-1 alpha-2 (e.g. RO, US)">
                  <Input value={a.country_of_registration || ""} disabled={!isOwner} maxLength={2} placeholder="RO" onChange={(e) => setA({ ...a, country_of_registration: e.target.value.toUpperCase() })} data-testid="profile-country" />
                </Field>
                <Field label="Operating region">
                  <Select value={a.operating_region || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, operating_region: e.target.value || null })} data-testid="profile-operating-region">
                    <option value="">—</option>
                    {(enums.operating_regions || []).map((r) => <option key={r} value={r}>{r}</option>)}
                  </Select>
                </Field>
                <Field label="Workspace currency"><Input value={tenant?.currency || "EUR"} disabled placeholder="Set in Settings → Profile" data-testid="profile-currency" /></Field>
              </div>
            </Surface>

            <Surface className="p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Airframe</div>
              <div className="text-xs text-zinc-500 mb-4">Total time, cycles, landings — used by the parts/maintenance forecast engine in Phase 1C.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 md:grid-cols-4 gap-4">
                <Field label="Airframe total hours"><Input type="number" step="0.1" value={a.airframe_total_hours ?? ""} disabled={!isOwner} onChange={(e) => setA({ ...a, airframe_total_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="profile-airframe-hours" /></Field>
                <Field label="Tach time"><Input type="number" step="0.1" value={a.tach_time ?? ""} disabled={!isOwner} onChange={(e) => setA({ ...a, tach_time: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="profile-tach" /></Field>
                <Field label="Total cycles"><Input type="number" value={a.total_cycles ?? ""} disabled={!isOwner} onChange={(e) => setA({ ...a, total_cycles: e.target.value === "" ? null : parseInt(e.target.value, 10) })} data-testid="profile-cycles" /></Field>
                <Field label="Total landings"><Input type="number" value={a.total_landings ?? ""} disabled={!isOwner} onChange={(e) => setA({ ...a, total_landings: e.target.value === "" ? null : parseInt(e.target.value, 10) })} data-testid="profile-landings" /></Field>
              </div>
            </Surface>

            <Surface className="p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Engine</div>
              <div className="text-xs text-zinc-500 mb-4">Engine identity and TBO interval — drives "due in X hrs" alerts in Phase 1C.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Manufacturer"><Input value={a.engine_manufacturer || ""} disabled={!isOwner} placeholder="Lycoming, Continental, Rotax…" onChange={(e) => setA({ ...a, engine_manufacturer: e.target.value })} data-testid="profile-engine-mfr" /></Field>
                <Field label="Model"><Input value={a.engine_model || ""} disabled={!isOwner} placeholder="O-360-A4M" onChange={(e) => setA({ ...a, engine_model: e.target.value })} data-testid="profile-engine-model" /></Field>
                <Field label="Serial"><Input value={a.engine_serial || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, engine_serial: e.target.value })} data-testid="profile-engine-serial" /></Field>
                <Field label="Total hours"><Input type="number" step="0.1" value={a.engine_total_hours ?? ""} disabled={!isOwner} onChange={(e) => setA({ ...a, engine_total_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="profile-engine-hours" /></Field>
                <Field label="TBO hours"><Input type="number" step="0.1" value={a.engine_tbo_hours ?? ""} disabled={!isOwner} onChange={(e) => setA({ ...a, engine_tbo_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="profile-engine-tbo-h" /></Field>
                <Field label="TBO calendar (next)"><Input type="date" value={a.engine_tbo_calendar || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, engine_tbo_calendar: e.target.value })} data-testid="profile-engine-tbo-cal" /></Field>
              </div>
            </Surface>

            <Surface className="p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Propeller</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Manufacturer"><Input value={a.propeller_manufacturer || ""} disabled={!isOwner} placeholder="McCauley, Hartzell…" onChange={(e) => setA({ ...a, propeller_manufacturer: e.target.value })} data-testid="profile-prop-mfr" /></Field>
                <Field label="Model"><Input value={a.propeller_model || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, propeller_model: e.target.value })} data-testid="profile-prop-model" /></Field>
                <Field label="Serial"><Input value={a.propeller_serial || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, propeller_serial: e.target.value })} data-testid="profile-prop-serial" /></Field>
                <Field label="Total hours"><Input type="number" step="0.1" value={a.propeller_total_hours ?? ""} disabled={!isOwner} onChange={(e) => setA({ ...a, propeller_total_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="profile-prop-hours" /></Field>
                <Field label="Overhaul due"><Input type="date" value={a.propeller_overhaul_due || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, propeller_overhaul_due: e.target.value })} data-testid="profile-prop-overhaul" /></Field>
              </div>
            </Surface>

            <Surface className="p-6">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Avionics</div>
                {isOwner && <Btn variant="ghost" onClick={addAvionics} data-testid="add-avionics-btn"><Plus size={12} className="inline mr-1" />Add unit</Btn>}
              </div>
              <div className="text-xs text-zinc-500 mb-4">Installed nav/com units, transponders, autopilots, MFDs — anything with its own serial + AD program.</div>
              {(a.avionics || []).length === 0 ? (
                <div className="text-sm text-zinc-500 py-4">No avionics declared.</div>
              ) : (
                <div className="space-y-3" data-testid="avionics-list">
                  {(a.avionics || []).map((av, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-3 items-end border border-zinc-800 p-3">
                      <div className="col-span-4"><Field label="Name"><Input value={av.name || ""} disabled={!isOwner} placeholder="Garmin G5" onChange={(e) => updateAvionics(idx, { name: e.target.value })} data-testid={`avionics-name-${idx}`} /></Field></div>
                      <div className="col-span-3"><Field label="Serial"><Input value={av.serial || ""} disabled={!isOwner} onChange={(e) => updateAvionics(idx, { serial: e.target.value })} /></Field></div>
                      <div className="col-span-4"><Field label="Notes"><Input value={av.notes || ""} disabled={!isOwner} onChange={(e) => updateAvionics(idx, { notes: e.target.value })} /></Field></div>
                      {isOwner && <button onClick={() => removeAvionics(idx)} className="col-span-1 h-9 text-zinc-500 hover:text-red-400 flex items-center justify-center" data-testid={`avionics-remove-${idx}`}><Trash size={14} /></button>}
                    </div>
                  ))}
                </div>
              )}
            </Surface>

            <Surface className="p-6">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Supplemental Type Certificates (STCs)</div>
                {isOwner && <Btn variant="ghost" onClick={addStc} data-testid="add-stc-btn"><Plus size={12} className="inline mr-1" />Add STC</Btn>}
              </div>
              <div className="text-xs text-zinc-500 mb-4">Installed modifications (auto-fuel STC, glass-panel retrofit, gross-weight increase, etc.) — each links to its own AD program.</div>
              {(a.stcs || []).length === 0 ? (
                <div className="text-sm text-zinc-500 py-4">No STCs declared.</div>
              ) : (
                <div className="space-y-3" data-testid="stcs-list">
                  {(a.stcs || []).map((st, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-3 items-end border border-zinc-800 p-3">
                      <div className="col-span-3"><Field label="STC reference"><Input value={st.reference || ""} disabled={!isOwner} placeholder="SA09112SC" onChange={(e) => updateStc(idx, { reference: e.target.value })} data-testid={`stc-ref-${idx}`} /></Field></div>
                      <div className="col-span-4"><Field label="Description"><Input value={st.description || ""} disabled={!isOwner} onChange={(e) => updateStc(idx, { description: e.target.value })} /></Field></div>
                      <div className="col-span-2"><Field label="Authority"><Input value={st.issuing_authority || ""} disabled={!isOwner} placeholder="FAA / EASA" onChange={(e) => updateStc(idx, { issuing_authority: e.target.value })} /></Field></div>
                      <div className="col-span-2"><Field label="Installed"><Input type="date" value={st.installed_date || ""} disabled={!isOwner} onChange={(e) => updateStc(idx, { installed_date: e.target.value })} /></Field></div>
                      {isOwner && <button onClick={() => removeStc(idx)} className="col-span-1 h-9 text-zinc-500 hover:text-red-400 flex items-center justify-center" data-testid={`stc-remove-${idx}`}><Trash size={14} /></button>}
                    </div>
                  ))}
                </div>
              )}
            </Surface>

            <Surface className="p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Inspections & expirations</div>
              <div className="text-xs text-zinc-500 mb-4">All recurring airworthiness checks. The alerts engine surfaces these at 30/7/0 days.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Registration expiry"><Input type="date" value={a.registration_expiry || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, registration_expiry: e.target.value })} data-testid="profile-reg-expiry" /></Field>
                <Field label="Last annual inspection"><Input type="date" value={a.last_annual_inspection || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, last_annual_inspection: e.target.value })} data-testid="profile-last-annual" /></Field>
                <Field label="Next annual due"><Input type="date" value={a.next_annual_due || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, next_annual_due: e.target.value })} data-testid="profile-next-annual" /></Field>
                <Field label="Last 100h inspection"><Input type="date" value={a.last_100h_inspection || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, last_100h_inspection: e.target.value })} data-testid="profile-last-100h" /></Field>
                <Field label="ELT battery expiry"><Input type="date" value={a.elt_expiry || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, elt_expiry: e.target.value })} data-testid="profile-elt" /></Field>
                <Field label="Survival kit expiry"><Input type="date" value={a.survival_kit_expiry || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, survival_kit_expiry: e.target.value })} data-testid="profile-survival" /></Field>
                <Field label="Transponder check due"><Input type="date" value={a.transponder_check_due || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, transponder_check_due: e.target.value })} data-testid="profile-xpdr" /></Field>
                <Field label="Altimeter check due"><Input type="date" value={a.altimeter_check_due || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, altimeter_check_due: e.target.value })} data-testid="profile-altim" /></Field>
                <Field label="Pitot/static check due"><Input type="date" value={a.pitot_static_check_due || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, pitot_static_check_due: e.target.value })} data-testid="profile-pitot" /></Field>
                <Field label="Maintenance provider"><Input value={a.maintenance_provider || ""} disabled={!isOwner} onChange={(e) => setA({ ...a, maintenance_provider: e.target.value })} data-testid="profile-mx-provider" /></Field>
              </div>
            </Surface>

            {isOwner && (
              <div className="flex justify-end gap-2">
                <Btn variant="ghost" onClick={() => setDocAIOpen(true)} data-testid="open-doc-ai-btn">
                  <Sparkle size={14} className="inline mr-1.5" />Extract from document
                </Btn>
                <Btn onClick={saveProfile} data-testid="save-profile-btn">Save profile</Btn>
              </div>
            )}
          </>
        )}

        {tab === "Maintenance" && (
          <MaintenanceTab aircraftId={id} isOwner={isOwner} aircraft={a} />
        )}

        {tab === "Airworthiness" && (
          <AirworthinessTab aircraftId={id} isOwner={isOwner} />
        )}

        {tab === "Work orders" && (
          <WorkOrdersTab aircraftId={id} isOwner={isOwner} aircraft={a} />
        )}

        {tab === "AI Assistant" && (
          <AIAssistantTab aircraftId={id} isOwner={isOwner} aircraft={a} />
        )}

        {tab === "Documents" && (
          <AircraftDocumentsTab aircraftId={id} isOwner={isOwner} aircraft={a} onAircraftUpdated={load} />
        )}

        {tab === "Reserves" && (
          <Surface className="p-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Reserve fund balances & targets · {a.registration}</div>
            <div className="text-xs text-zinc-500 mb-4">Confirmed settlements are automatically split into these buckets in proportion to each target&apos;s share of the total target.</div>
            <div className="space-y-4">
              {(() => {
                const total = reserveKeys.reduce((s, k) => s + (parseFloat(a.reserve_targets[k]) || 0), 0);
                return reserveKeys.map((k) => {
                  const tgt = parseFloat(a.reserve_targets[k]) || 0;
                  const pct = total > 0 ? (tgt / total) * 100 : 0;
                  return (
                <div key={k}>
                  <div className="flex justify-between items-baseline mb-2">
                    <div className="text-sm">{reserveLabels[k]} <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-amber font-mono" data-testid={`weight-${k}`}>{pct.toFixed(1)}% allocation</span></div>
                    <div className="font-mono text-sm"><span className="text-amber">{fmtMoney(a.reserves[k])}</span> <span className="text-zinc-600">/ {fmtMoney(a.reserve_targets[k])}</span></div>
                  </div>
                  <div className="h-1.5 bg-zinc-900 border border-zinc-800 mb-2">
                    <div className="h-full bg-amber" style={{ width: `${Math.min(100, dash.reserve_pcts[k])}%` }} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Balance"><CurrencyInput disabled={!isOwner} value={a.reserves[k]} onChange={(v) => setA({ ...a, reserves: { ...a.reserves, [k]: v } })} data-testid={`reserve-balance-${k}`} /></Field>
                    <Field label="Target"><Input type="number" disabled={!isOwner} value={a.reserve_targets[k]} onChange={(e) => setA({ ...a, reserve_targets: { ...a.reserve_targets, [k]: parseFloat(e.target.value || 0) } })} data-testid={`reserve-target-${k}`} /></Field>
                  </div>
                </div>
                  );
                });
              })()}
            </div>
            {isOwner && <div className="mt-4 flex justify-end"><Btn onClick={saveReserves} data-testid="save-reserves-btn">Save reserves</Btn></div>}
          </Surface>
        )}

        {tab === "Cost rates" && (
          <Surface className="p-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-4">Cost contribution rates · {a.registration}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 md:grid-cols-3 gap-4">
              <Field label={`Variable ${tenant?.currency || "EUR"} / hr`}><CurrencyInput disabled={!isOwner} value={a.cost_settings.rate_variable_per_hour} onChange={(v) => setA({ ...a, cost_settings: { ...a.cost_settings, rate_variable_per_hour: v } })} /></Field>
              <Field label={`Annual fixed ${tenant?.currency || "EUR"}`}><CurrencyInput disabled={!isOwner} value={a.cost_settings.annual_fixed_amount} onChange={(v) => setA({ ...a, cost_settings: { ...a.cost_settings, annual_fixed_amount: v } })} /></Field>
              <Field label={`Onboarding ${tenant?.currency || "EUR"}`}><CurrencyInput disabled={!isOwner} value={a.cost_settings.onboarding_amount} onChange={(v) => setA({ ...a, cost_settings: { ...a.cost_settings, onboarding_amount: v } })} /></Field>
              <Field label={`Instructor ${tenant?.currency || "EUR"} / hr`}><CurrencyInput disabled={!isOwner} value={a.cost_settings.instructor_per_hour} onChange={(v) => setA({ ...a, cost_settings: { ...a.cost_settings, instructor_per_hour: v } })} /></Field>
              <Field label="Buffer %"><Input type="number" step="0.1" disabled={!isOwner} value={a.cost_settings.buffer_pct} onChange={(e) => setA({ ...a, cost_settings: { ...a.cost_settings, buffer_pct: parseFloat(e.target.value || 0) } })} /></Field>
              <Field label={`Hangar fee ${tenant?.currency || "EUR"} / mo`}>
                <CurrencyInput disabled={!isOwner}
                  value={a.cost_settings.hangar_fee_amount ?? 0}
                  onChange={(v) => setA({ ...a, cost_settings: { ...a.cost_settings, hangar_fee_amount: v } })}
                  data-testid="hangar-fee-input" />
              </Field>
              <Field label="Hangar fee day (1-28)">
                <Input type="number" min="1" max="28" disabled={!isOwner}
                  value={a.cost_settings.hangar_fee_day ?? 1}
                  onChange={(e) => setA({ ...a, cost_settings: { ...a.cost_settings, hangar_fee_day: parseInt(e.target.value || 1, 10) } })}
                  data-testid="hangar-fee-day-input" />
              </Field>
              <Field label={`CAMO fee ${tenant?.currency || "EUR"} / yr`}>
                <CurrencyInput disabled={!isOwner}
                  value={a.cost_settings.camo_fee_amount ?? 0}
                  onChange={(v) => setA({ ...a, cost_settings: { ...a.cost_settings, camo_fee_amount: v } })}
                  data-testid="camo-fee-input" />
              </Field>
              <Field label="CAMO fee day (1-28)">
                <Input type="number" min="1" max="28" disabled={!isOwner}
                  value={a.cost_settings.camo_fee_day ?? 1}
                  onChange={(e) => setA({ ...a, cost_settings: { ...a.cost_settings, camo_fee_day: parseInt(e.target.value || 1, 10) } })}
                  data-testid="camo-fee-day-input" />
              </Field>
            </div>
            <div className="mt-4 text-xs text-zinc-500 font-mono space-y-1">
              <div>
                Hangar auto-posts <span className="text-amber">{fmtMoney(a.cost_settings.hangar_fee_amount || 0)}</span>/mo on day{" "}
                <span className="text-amber">{a.cost_settings.hangar_fee_day ?? 1}</span> · annual{" "}
                <span className="text-amber">{fmtMoney((a.cost_settings.hangar_fee_amount || 0) * 12)}</span>
              </div>
              <div>
                CAMO auto-posts <span className="text-amber">{fmtMoney((a.cost_settings.camo_fee_amount || 0) / 12)}</span>/mo (1/12 of annual) on day{" "}
                <span className="text-amber">{a.cost_settings.camo_fee_day ?? 1}</span> · annual{" "}
                <span className="text-amber">{fmtMoney(a.cost_settings.camo_fee_amount || 0)}</span>
              </div>
              {!a.accounting_start_date && <div className="text-zinc-600">⚠ Set accounting start date in Overview before postings begin.</div>}
            </div>
            {isOwner && <div className="mt-4 flex justify-end"><Btn onClick={saveCost} data-testid="save-cost-btn">Save rates</Btn></div>}
          </Surface>
        )}

        {tab === "Members" && (
          <>
            {isOwner && (
              <Surface className="p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-4">Add member to {a.registration}</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                  <Field label="Participant">
                    <Select value={memForm.user_id} onChange={(e) => setMemForm({ ...memForm, user_id: e.target.value })}>
                      <option value="">Select…</option>
                      {memberOptions.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
                    </Select>
                  </Field>
                  <Field label="Status">
                    <Select value={memForm.status} onChange={(e) => setMemForm({ ...memForm, status: e.target.value })}>
                      <option value="pending">pending</option>
                      <option value="active">active</option>
                      <option value="training_only">training only</option>
                    </Select>
                  </Field>
                  <Field label="Named on insurance">
                    <Select value={memForm.named_on_insurance ? "yes" : "no"} onChange={(e) => setMemForm({ ...memForm, named_on_insurance: e.target.value === "yes" })}>
                      <option value="no">No</option><option value="yes">Yes</option>
                    </Select>
                  </Field>
                  <Btn onClick={addMember} data-testid="add-member-btn"><Plus size={14} className="inline mr-1.5" />Add member</Btn>
                </div>
              </Surface>
            )}
            <Surface>
              <div className="overflow-x-auto"><table className="w-full text-sm min-w-[680px]">
                <thead className="border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                  <tr>
                    <th className="text-left py-3 px-4">Pilot</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Insurance</th>
                    <th className="text-left py-3 px-4">Contract</th>
                    <th className="text-left py-3 px-4">Last flight</th>
                    <th className="text-right py-3 px-4">Hrs/type</th>
                    <th className="text-left py-3 px-4">Compliance</th>
                    {isOwner && <th className="text-right py-3 px-4 w-24"></th>}
                  </tr>
                </thead>
                <tbody data-testid="members-table">
                  {members.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-zinc-500">No members on this aircraft yet.</td></tr>}
                  {members.map((m) => {
                    const today = new Date().toISOString().substring(0, 10);
                    const contractExpired = m.contract_expiry && m.contract_expiry < today;
                    const contractSoon = m.contract_expiry && !contractExpired && (new Date(m.contract_expiry) - new Date()) < 60 * 86400000;
                    return (
                    <tr key={m.user_id} className="border-b border-zinc-900">
                      <td className="py-2.5 px-4">{m.user_name} <span className="text-xs text-zinc-500">· {m.user_email}</span></td>
                      <td className="py-2.5 px-4">
                        {isOwner ? (
                          <Select value={m.status} onChange={(e) => updateMember(m.user_id, { status: e.target.value })} data-testid={`member-status-${m.user_id}`}>
                            {["pending", "active", "training_only", "suspended"].map((s) => <option key={s} value={s}>{s}</option>)}
                          </Select>
                        ) : <StatusTag status={m.status} />}
                      </td>
                      <td className="py-2.5 px-4">
                        {isOwner ? (
                          <input type="checkbox" checked={m.named_on_insurance} onChange={(e) => updateMember(m.user_id, { named_on_insurance: e.target.checked })} />
                        ) : (m.named_on_insurance ? <span className="text-green-400 text-xs">Named</span> : <span className="text-red-400 text-xs">Not named</span>)}
                      </td>
                      <td className="py-2.5 px-4">
                        {m.contract_expiry ? (
                          <div className="flex items-center gap-2">
                            <a href={`${BACKEND_URL}/api/files/${m.contract_storage_path}`} target="_blank" rel="noreferrer" className="text-zinc-300 hover:text-amber" title={m.contract_filename} data-testid={`contract-dl-${m.user_id}`}>
                              <FileArrowDown size={14} />
                            </a>
                            <span className={`font-mono text-xs ${contractExpired ? "text-red-400" : contractSoon ? "text-amber" : "text-zinc-300"}`}>
                              exp {m.contract_expiry}{contractExpired && " · expired"}
                            </span>
                          </div>
                        ) : <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">none</span>}
                        {isOwner && (
                          <button onClick={() => setContractFor(m)} className="block mt-1 text-[10px] uppercase tracking-[0.2em] text-amber hover:underline" data-testid={`contract-upload-${m.user_id}`}>
                            <FileArrowUp size={11} className="inline mr-1" />{m.contract_expiry ? "Renew" : "Upload"}
                          </button>
                        )}
                      </td>
                      <td className="py-2.5 px-4 font-mono text-zinc-400">{fmtDate(m.last_flight_date)}</td>
                      <td className="py-2.5 px-4 text-right font-mono">{fmtNum(m.total_hours_on_type, 1)}</td>
                      <td className="py-2.5 px-4">
                        {(m.compliance_issues || []).length === 0 ? <span className="text-green-400 text-xs uppercase tracking-wider">OK</span>
                          : <span className="text-amber text-[10px] font-mono">{m.compliance_issues.join(", ")}</span>}
                      </td>
                      {isOwner && <td className="py-2.5 px-4 text-right"><button onClick={() => removeMember(m.user_id)} className="text-zinc-500 hover:text-red-400" data-testid={`remove-member-${m.user_id}`}><Trash size={14} /></button></td>}
                    </tr>
                  );})}
                </tbody>
              </table></div>
            </Surface>
          </>
        )}

        {tab === "Bank" && (
          <Surface className="p-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Bank account · {a.registration}</div>
            <div className="text-sm text-zinc-500 mb-6">Settlements generated for this aircraft will display these details alongside the Revolut reference.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Account holder"><Input disabled={!isOwner} value={a.bank_account.account_holder || ""} onChange={(e) => setA({ ...a, bank_account: { ...a.bank_account, account_holder: e.target.value } })} data-testid="bank-holder" /></Field>
              <Field label="Bank name"><Input disabled={!isOwner} value={a.bank_account.bank_name || ""} onChange={(e) => setA({ ...a, bank_account: { ...a.bank_account, bank_name: e.target.value } })} /></Field>
              <Field label="IBAN"><Input disabled={!isOwner} value={a.bank_account.iban || ""} onChange={(e) => setA({ ...a, bank_account: { ...a.bank_account, iban: e.target.value } })} data-testid="bank-iban" /></Field>
              <Field label="SWIFT/BIC"><Input disabled={!isOwner} value={a.bank_account.swift || ""} onChange={(e) => setA({ ...a, bank_account: { ...a.bank_account, swift: e.target.value } })} /></Field>
              <div className="col-span-2"><Field label="Notes"><Textarea rows={2} disabled={!isOwner} value={a.bank_account.notes || ""} onChange={(e) => setA({ ...a, bank_account: { ...a.bank_account, notes: e.target.value } })} /></Field></div>
            </div>
            {isOwner && <div className="mt-4 flex justify-end"><Btn onClick={saveBank} data-testid="save-bank-btn">Save bank account</Btn></div>}
          </Surface>
        )}

        {tab === "Expenses" && (
          <>
            {isOwner && (
              <Surface className="p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-4">Log operating expense for {a.registration}</div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <Field label="Date"><Input type="date" value={exForm.date} onChange={(e) => setExForm({ ...exForm, date: e.target.value })} /></Field>
                  <Field label="Category">
                    <Select value={exForm.category} onChange={(e) => setExForm({ ...exForm, category: e.target.value })}>
                      {["maintenance", "insurance", "camo", "hangar", "improvement", "other"].map((c) => <option key={c} value={c}>{c}</option>)}
                    </Select>
                  </Field>
                  <Field label={`Amount (${tenant?.currency || "EUR"})`}><CurrencyInput data-testid="expense-amount" value={exForm.amount_amount} onChange={(v) => setExForm({ ...exForm, amount_amount: v })} /></Field>
                  <div className="col-span-2"><Field label="Description"><Input value={exForm.description} onChange={(e) => setExForm({ ...exForm, description: e.target.value })} /></Field></div>
                </div>
                <div className="mt-4 flex justify-end"><Btn onClick={addExpense} data-testid="add-expense-btn">Add expense</Btn></div>
              </Surface>
            )}
            <Surface>
              <div className="overflow-x-auto"><table className="w-full text-sm min-w-[680px]">
                <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-800">
                  <th className="text-left py-2.5 px-6">Date</th><th className="text-left py-2.5 px-6">Category</th><th className="text-left py-2.5 px-6">Description</th><th className="text-right py-2.5 px-6">Amount</th>{isOwner && <th></th>}
                </tr></thead>
                <tbody>
                  {expenses.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-zinc-500">No expenses logged.</td></tr>}
                  {expenses.map((e) => (
                    <tr key={e.id} className="border-b border-zinc-900">
                      <td className="py-2.5 px-6 font-mono">{e.date}</td>
                      <td className="py-2.5 px-6 capitalize">{e.category}</td>
                      <td className="py-2.5 px-6 text-zinc-400">{e.description || "—"}</td>
                      <td className="py-2.5 px-6 text-right font-mono text-amber">{fmtMoney(e.amount_amount)}</td>
                      {isOwner && <td className="py-2.5 px-4 text-right"><button onClick={() => removeExpense(e.id)} className="text-zinc-500 hover:text-red-400"><Trash size={14} /></button></td>}
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </Surface>
          </>
        )}

        {tab === "Contributions" && (
          <>
            {isOwner && (
              <Surface className="p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Record member contribution · {a.registration}</div>
                <div className="text-xs text-zinc-500 mb-4">Initial deposits and top-ups go directly into the chosen reserve bucket. Amounts are in your workspace currency.</div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                  <Field label="Member">
                    <Select value={cForm.user_id} onChange={(e) => setCForm({ ...cForm, user_id: e.target.value })} data-testid="contrib-member">
                      <option value="">Select…</option>
                      {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.user_name}</option>)}
                    </Select>
                  </Field>
                  <Field label="Date"><Input type="date" value={cForm.date} onChange={(e) => setCForm({ ...cForm, date: e.target.value })} /></Field>
                  <Field label={`Amount (${tenant?.currency || "EUR"})`}><CurrencyInput data-testid="contrib-amount" value={cForm.amount_amount} onChange={(v) => setCForm({ ...cForm, amount_amount: v })} /></Field>
                  <Field label="Bucket">
                    <Select value={cForm.bucket} onChange={(e) => setCForm({ ...cForm, bucket: e.target.value })} data-testid="contrib-bucket">
                      <option value="engine">engine</option>
                      <option value="prop">propeller</option>
                      <option value="maintenance">maintenance</option>
                      <option value="improvement">improvement</option>
                      <option value="general">general (no bucket)</option>
                    </Select>
                  </Field>
                  <Field label="Kind">
                    <Select value={cForm.kind} onChange={(e) => setCForm({ ...cForm, kind: e.target.value })}>
                      <option value="initial">initial deposit</option>
                      <option value="topup">top-up</option>
                      <option value="settlement_payment">settlement payment</option>
                    </Select>
                  </Field>
                  <Btn onClick={addContribution} data-testid="contrib-add-btn">Record</Btn>
                </div>
                <div className="mt-3"><Field label="Notes"><Input value={cForm.notes} onChange={(e) => setCForm({ ...cForm, notes: e.target.value })} /></Field></div>
              </Surface>
            )}
            <Surface>
              <div className="overflow-x-auto"><table className="w-full text-sm min-w-[680px]">
                <thead className="border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                  <tr>
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Member</th>
                    <th className="text-left py-3 px-4">Bucket</th>
                    <th className="text-left py-3 px-4">Kind</th>
                    <th className="text-right py-3 px-4">Amount</th>
                    <th className="text-left py-3 px-4">Notes</th>
                    {isOwner && <th></th>}
                  </tr>
                </thead>
                <tbody data-testid="contributions-table">
                  {contributions.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-zinc-500">No contributions recorded yet.</td></tr>}
                  {contributions.map((c) => (
                    <tr key={c.id} className="border-b border-zinc-900">
                      <td className="py-2.5 px-4 font-mono">{c.date}</td>
                      <td className="py-2.5 px-4">{c.user_name}</td>
                      <td className="py-2.5 px-4 capitalize text-amber">{c.bucket}</td>
                      <td className="py-2.5 px-4 text-xs uppercase tracking-wider text-zinc-400">{c.kind?.replace("_", " ")}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-amber">{fmtMoney(c.amount_amount)}</td>
                      <td className="py-2.5 px-4 text-zinc-400 text-xs">{c.notes || "—"}</td>
                      {isOwner && <td className="py-2.5 px-4 text-right"><button onClick={() => removeContribution(c.id)} className="text-zinc-500 hover:text-red-400" data-testid={`contrib-del-${c.id}`}><Trash size={14} /></button></td>}
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </Surface>
          </>
        )}
      </div>

      {contractFor && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="contract-modal">
          <Surface className="w-full max-w-lg p-8">
            <h3 className="text-xl font-medium mb-2">{contractFor.contract_expiry ? "Renew" : "Upload"} contract</h3>
            <div className="text-sm text-zinc-400 mb-6">
              {contractFor.user_name} · {a.registration}.<br/>
              <span className="text-zinc-500">Uploading a new contract automatically generates an annual fee settlement of <span className="text-amber font-mono">{fmtMoney(a.cost_settings.annual_fixed_amount)}</span> against {contractFor.user_name}.</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Signed on"><Input type="date" value={contractForm.signed_date} onChange={(e) => setContractForm({ ...contractForm, signed_date: e.target.value })} data-testid="contract-signed-date" /></Field>
              <Field label="Valid until"><Input type="date" value={contractForm.expiry_date} onChange={(e) => setContractForm({ ...contractForm, expiry_date: e.target.value })} data-testid="contract-expiry-date" /></Field>
              <div className="col-span-2">
                <Field label="Signed contract (PDF/image)">
                  <input type="file" data-testid="contract-file-input" onChange={(e) => setContractForm({ ...contractForm, file: e.target.files[0] })}
                         className="text-sm text-zinc-300 file:bg-amber file:text-black file:border-0 file:px-3 file:py-1.5 file:mr-3 file:font-medium file:text-xs file:uppercase file:tracking-wider" />
                </Field>
              </div>
            </div>
            {contractMsg && <div className="mt-3 text-sm text-red-400 font-mono" data-testid="contract-error">{contractMsg}</div>}
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => { setContractFor(null); setContractMsg(""); }}>Cancel</Btn>
              <Btn onClick={submitContract} data-testid="contract-submit-btn">Upload & generate fee</Btn>
            </div>
          </Surface>
        </div>
      )}

      {docAIOpen && (
        <DocAIUploadModal
          aircraftId={id}
          onClose={() => setDocAIOpen(false)}
          onApplied={() => { load(); }}
        />
      )}
    </div>
  );
}
